import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, switchMap, take, catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { Auth, onAuthStateChanged, signOut } from '@angular/fire/auth';
import { environment } from '../../../environments/environment';
import { OrganizationContextService } from '../services/organization-context.service';
import { WebSocketService } from '../services/websocket.service';

/**
 * Auth Interceptor
 * Adds Firebase authentication token and organization context to all API requests
 * Based on:
 * - agent-os/specs/01-user-authentication-sso/spec.md
 * - agent-os/specs/2026-01-07-organization-context-middleware/spec.md
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  /**
   * Routes that are part of the auth flow itself — never redirect away from
   * these on a 401, or the user loses access to the page that would fix it.
   */
  private static readonly AUTH_ROUTES: readonly string[] = [
    '/login',
    '/reset-password',
    '/forgot-password',
    '/signup',
    '/accept-invitation',
  ];

  /**
   * Promise that resolves once Firebase Auth has finished restoring session state.
   * Prevents race condition where API calls fire before currentUser is available on refresh.
   */
  private authReady: Promise<void>;

  constructor(
    private auth: Auth,
    private organizationContext: OrganizationContextService,
    private websocketService: WebSocketService,
    private router: Router
  ) {
    // Wait for the first auth state emission — this resolves once Firebase
    // has finished checking for a persisted session (whether user or null).
    // Timeout after 5 seconds to prevent blocking requests if Firebase is slow to initialize.
    this.authReady = new Promise<void>(resolve => {
      let resolved = false;
      const unsubscribe = onAuthStateChanged(this.auth, () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          unsubscribe();
          resolve();
        }
      });
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          unsubscribe();
          resolve();
        }
      }, 5000);
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only add token to requests going to our API
    if (!req.url.startsWith(environment.apiUrl)) {
      return next.handle(req);
    }

    // Wait for auth to be ready, then get token
    return from(this.getToken()).pipe(
      take(1),
      switchMap(token => {
        // Prepare headers object
        const headers: { [key: string]: string } = {
          // Signals to the Hub that responses targeted at this client should
          // use web-flavored return URLs (Angular's own routes), not the
          // mobile bouncer at /payments/return/:flow. See billing-payments § 1.0.1.
          'X-Client-Platform': 'web',
        };

        // Add Authorization header if token exists
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Add X-Organization-Id header if organization context is set
        const organizationId = this.organizationContext.getCurrentOrganizationId();
        if (organizationId) {
          headers['X-Organization-Id'] = organizationId;
        }

        // Echo our socket.io connection id so the Hub can stamp it onto
        // the resulting realtime event payloads. Receiving clients
        // suppress only same-environment self-toasts off this id; the
        // same Firebase user on mobile still gets a toast.
        const socketId = this.websocketService.getSocketId();
        if (socketId) {
          headers['X-Client-Socket-Id'] = socketId;
        }

        // Clone request with headers if any were added
        const outgoing = Object.keys(headers).length > 0
          ? req.clone({ setHeaders: headers })
          : req;

        return next.handle(outgoing).pipe(
          catchError((err: HttpErrorResponse) => this.handleAuthError(err))
        );
      })
    );
  }

  /**
   * The Hub rejects flagged accounts on every request, but Firebase still holds
   * a valid session — sign out locally so the UI can't sit in a dead state.
   * Only PASSWORD_RESET_REQUIRED is handled; a plain 401 may be a stale token
   * that `getIdToken()` refreshes on its own.
   */
  private handleAuthError(err: HttpErrorResponse): Observable<never> {
    if (err.status === 401 && err.error?.code === 'PASSWORD_RESET_REQUIRED') {
      // A flagged account keeps a valid Firebase session, so background calls
      // (the auth listener's organization fetch, for one) 401 on every page —
      // including the reset page itself. Redirecting from there would strand
      // the user away from the only route that clears the flag.
      const onAuthRoute = AuthInterceptor.AUTH_ROUTES.some(route =>
        this.router.url.split('?')[0].startsWith(route),
      );

      // Always drop the organization id — it persists in localStorage and would
      // otherwise ride along on the next user's requests. Safe on every route.
      this.organizationContext.clearCurrentOrganization();

      // Signing out is deliberately NOT done on auth routes: the reset page
      // re-authenticates via signIn(), and a background 401 landing just after
      // that would sign the user straight back out.
      if (!onAuthRoute) {
        signOut(this.auth).finally(() => {
          this.router.navigate(['/login'], {
            queryParams: { reason: 'password-reset-required' },
          });
        });
      }
    }

    return throwError(() => err);
  }

  /**
   * Get current Firebase ID token
   * Waits for auth initialization on first call (handles page refresh race condition)
   * Automatically refreshes token if expired
   */
  private async getToken(): Promise<string | null> {
    // Wait for Firebase to finish restoring session before checking currentUser
    await this.authReady;

    const user = this.auth.currentUser;

    if (!user) {
      return null;
    }

    try {
      // Get token, will automatically refresh if expired
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting Firebase token:', error);
      return null;
    }
  }
}
